import { teal } from "@mui/material/colors";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { Folder, Insights, Memory } from "@mui/icons-material";
import React, { Suspense } from "react";
import { useLazyLoadQuery } from "react-relay";
import { SidebarQuery } from "./__generated__/SidebarQuery.graphql";
import { graphql } from "babel-plugin-relay/macro";

const listItemButtonSx = {
  borderRadius: 2.5,
  "&.active": {
    bgcolor: "action.selected",
  },
};

interface SidebarProps {
  organization?: string;
  sidebarVisible: boolean;
}

export default function Sidebar(props: SidebarProps) {
  const theme = useTheme();
  const { organization, sidebarVisible } = props;

  if (!organization) {
    return null;
  }

  return (
    <Box
      sx={{
        display: {
          xs: sidebarVisible ? "block" : "none",
          sm: "block",
        },
        width: {
          xs: "100%",
          sm: theme.spacing(32),
        },
        height: "100%",
        position: "fixed",
        top: {
          xs: theme.spacing(6),
          sm: theme.spacing(8),
        },
        padding: theme.spacing(2),
        // Compensate for the shadow dropped by the toolbar
        pt: theme.spacing(3),
        background: teal[50],
        // We need to be below the app bar because it has shadow,
        // but above the rest of the content
        zIndex: theme.zIndex.appBar - 1,
      }}
    >
      <List
        disablePadding
        sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(1) }}
      >
        <ListItem disablePadding disableGutters>
          <ListItemButton
            component={NavLink}
            to={`/dashboard/${organization}`}
            end
            sx={listItemButtonSx}
          >
            <ListItemIcon>
              <Memory />
            </ListItemIcon>
            <ListItemText primary="Runners" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding disableGutters>
          <ListItemButton
            component={NavLink}
            to={`/dashboard/${organization}/insights`}
            end
            sx={listItemButtonSx}
          >
            <ListItemIcon>
              <Insights />
            </ListItemIcon>
            <ListItemText primary="Insights" />
          </ListItemButton>
        </ListItem>
        <Suspense
          fallback={
            <Skeleton
              variant="rounded"
              height={theme.spacing(6)}
              sx={{ borderRadius: 2.5 }}
            />
          }
        >
          <SidebarCacheEntries organization={organization} />
        </Suspense>
      </List>
    </Box>
  );
}

interface SidebarCacheEntriesProps {
  organization: string;
}

function SidebarCacheEntries(props: SidebarCacheEntriesProps) {
  const { organization } = props;

  const data = useLazyLoadQuery<SidebarQuery>(
    graphql`
      query SidebarQuery($name: String!) {
        organization(name: $name) {
          viewerCanAdminister
        }
      }
    `,
    { name: organization },
  );

  return (
    <>
      <Tooltip
        title={
          data.organization?.viewerCanAdminister
            ? ""
            : "Only organization admins can manage the cache entries"
        }
        arrow
      >
        <ListItem disablePadding disableGutters>
          <ListItemButton
            component={NavLink}
            to={`/dashboard/${organization}/cache`}
            sx={listItemButtonSx}
            disabled={!data.organization?.viewerCanAdminister}
          >
            <ListItemIcon>
              <Folder />
            </ListItemIcon>
            <ListItemText primary="Cache entries" />
          </ListItemButton>
        </ListItem>
      </Tooltip>
    </>
  );
}
